import {
  Briefcase,
  CaretDown,
  Link as LinkIcon,
  PlusCircle,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import { LinkedInLogin } from "common/components/LinkedInLogin";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { ManageSubscriptionDialog } from "common/components/subscriptions";
import { Button } from "common/components/ui/Button";
import { Dialog, DialogContent } from "common/components/ui/Dialog";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWhitelabelCustomization from "common/hooks/useWhitelabelCustomization";

import BrandedLoginUrl from "./BrandedLoginUrl";

export default function AddAccount() {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState<"brandedLogin" | "login" | "rental">(
    null,
  );
  const { whitelabelCustomization } = useWhitelabelCustomization();
  const { rental_subscription: rentedSubscription } =
    useSelectedWorkspaceContext();

  function closeModal() {
    setIsOpen(false);
  }

  const sharedStyles =
    "flex h-10 cursor-pointer items-center justify-center bg-black-950 text-button-sm transition-colors duration-300 ease-in-out hover:bg-black-700 focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-black-200 disabled:text-black-400";

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;

  return (
    <RestrictedComponent visibleForPermissions={["accounts_full"]}>
      {whitelabelCustomization ? (
        <Button
          leftIcon={<PlusCircle weight="fill" />}
          data-cy="add-account-button"
          onClick={() => {
            setAction("login");
            setIsOpen(true);
          }}
        >
          Add account
        </Button>
      ) : (
        <div className="group flex items-center overflow-hidden rounded-full text-white">
          <button
            type="button"
            onClick={() => {
              setAction("login");
              setIsOpen(true);
            }}
            className={clsx(sharedStyles, "w-full gap-x-2 pl-3 pr-2")}
            data-cy="add-account-button"
          >
            <PlusCircle weight="fill" size={20} /> Add account
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger
              className={clsx(
                sharedStyles,
                "group relative pl-2 pr-3 data-[state=open]:bg-black-700",
                // Separator
                "before:absolute before:left-0 before:h-5 before:w-px before:bg-black-700 before:opacity-100 before:transition-opacity before:duration-300 before:ease-in-out before:group-hover:opacity-0",
              )}
            >
              <CaretDown
                size={20}
                className="transition-transform group-data-[state=open]:rotate-180"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuList>
                <DropdownMenuItem
                  onClick={() => {
                    setAction("brandedLogin");
                    setIsOpen(true);
                  }}
                >
                  <LinkIcon />
                  Connect via link
                </DropdownMenuItem>

                <RenderIf condition={!isDesktop}>
                  <RestrictedComponent visibleForPermissions={["billing_full"]}>
                    <DropdownMenuItem
                      onClick={() => {
                        setAction("rental");
                        setIsOpen(true);
                      }}
                    >
                      <Briefcase />
                      Rent accounts
                    </DropdownMenuItem>
                  </RestrictedComponent>
                </RenderIf>
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {action === "rental" ? (
        <ManageSubscriptionDialog
          plan="avatar"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          fixedPricingPeriodInfo={
            rentedSubscription
              ? {
                  period: rentedSubscription.item_price.period,
                  period_unit: rentedSubscription.item_price.period_unit,
                }
              : { period: 1, period_unit: "month" }
          }
        />
      ) : (
        <Component open={isOpen} onOpenChange={setIsOpen}>
          <Content className={action === "brandedLogin" && "lg:w-[448px]"}>
            {action === "login" ? (
              <LinkedInLogin onSuccess={closeModal} />
            ) : (
              <BrandedLoginUrl goBack={closeModal} />
            )}
          </Content>
        </Component>
      )}
    </RestrictedComponent>
  );
}
